import React from "react";

import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { useNavigate } from "react-router-dom";

import { TaggedImage } from "constructs/TaggedImage";
import { Tag } from "constructs/Tag";
import ObfuscatedImageExplanation from "./ObfuscatedImageTooltip";
import { useDebouncedCallback } from "use-debounce";

export type ObfuscationStyle = "blur" | "hide" | number | string;

export type ObfuscationState = "on" | "partial" | "off";

export interface PostImagesProps {
  image?: TaggedImage;
  tagExclusions?: Array<Tag>;
  obfuscationMethod: ObfuscationStyle;
  height?: string | undefined;
  width?: string | undefined;
  link?: string | undefined;
  data?: any;
}

const PostImage = (props: PostImagesProps) => {
  const navigate = useNavigate();
  
  const { image, obfuscationMethod, tagExclusions, height, width } = props;
  let isSpecificImage: boolean = React.useMemo(() => toggleSpecificImage(image), [ image ]);

  const [userImageToggle, setUserImageToggle] =
    React.useState<ObfuscationState>("off");

  const [ touchingTooltip, setTouchingTooltip ] = React.useState(false);
  const [tooltipOpen, setTooltipOpen] = React.useState(false);

  // Here the hiding style is determined by the props (or or settings context)
  const hidingStyle = React.useMemo(
    () => updateImageStyle(obfuscationMethod),
    [obfuscationMethod]
  );

  const exclusionsList = React.useMemo(
    () => listExclusions(image, tagExclusions),
    [image, tagExclusions]
  );

  // const hasExclusions = React.useMemo(
  //   () => containsExclusions(image, tagExclusions),
  //   [image, tagExclusions]
  // );
  const hasExclusions = exclusionsList.length > 0;
  

  const showImage = React.useMemo(
    () => toggleShowImage(userImageToggle, hasExclusions),
    [userImageToggle, hasExclusions]
  );

  const handleMouseLeave =  useDebouncedCallback(() => {
    setTooltipOpen(false);
  }, 300);

  // const handleMouseLeaveTooltip = useDebouncedCallback(() => {
  //   setTooltipOpen(false);
  // }, 200);


  if (typeof image === "undefined") {
    return null;
  }

  const handleMouseEnter = () => {
    if (!hasExclusions) {
      return;
    }

    setTooltipOpen(true);
  };

  const handleMouseEnterTooltip = () => {
    handleMouseLeave.cancel();
  }

  const handleToggleImageState = (
    event: React.MouseEvent<HTMLButtonElement, React.MouseEvent>
  ) => {
    let newState = userImageToggle;
    if (userImageToggle === "off") {
      newState = "partial";
    } else if (userImageToggle === "partial") {
      newState = "on";
    } else if (userImageToggle === "on") {
      newState = "off";
    }

    event.stopPropagation();
    setUserImageToggle(newState);
  };

  const linkClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`${props.link}`, { state: props.data });
  };

  const ignoreClick = () => null;
  const fullBoxClick = props.link ? linkClick : ignoreClick;

  const updatePartialState = () => {
    let styles = {};

    switch (userImageToggle) {
      case "off":
        styles = { filter: `blur(${5}px)` };
        break;
      default:
        styles = {};
        break;
    }

    return styles;
  };

  // if (!props.image || !props.image.uri) {
  //   return null;
  // }

  function toggleSpecificImage(image: TaggedImage| undefined): boolean {
    const allowedImageTypes = [ ".jpg", ".avif", ".png", ".webp" ];
    return ( allowedImageTypes.some((imageType) => image?.uri?.endsWith(imageType)));
  };

  return (
    <Box
      overflow="hidden"
      width={width ?? "100%"}
      height={height ?? "100%" ?? "auto"}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      // onClick={ (event: any) => null }
    >
      <Tooltip
        open={tooltipOpen}
        title={
          <ObfuscatedImageExplanation
            imageTags={exclusionsList}
            userImageState={userImageToggle}
            onClick={handleToggleImageState}
            onMouseEnter={handleMouseEnterTooltip}
            // onMouseLeave={() => {}}
            
          />
        }
      >
        <span style={{ position: 'relative', display: 'block', width: "100%", height: "100%" }}>
          <Box
            sx={{ position: "absolute", zIndex: 10, pointerEvents: "none" }}
            height={height ?? "100%"}
            width={width ?? "100%"}
          >
            <svg width="100%" height="100%">
              {userImageToggle !== "on" && hasExclusions
                ? image.obfuscations
                    ?.filter(
                      (obfuscation) =>
                        exclusionsList.findIndex((exclusion) =>
                          obfuscation.tags?.includes(exclusion.id)
                        ) === -1
                    )
                    .map((obfuscation, index) => (
                      <circle
                        key={`${obfuscation.element}-${index}`}
                        cx={obfuscation.cx}
                        cy={obfuscation.cy}
                        r={obfuscation.r}
                        fill={"pink"}
                        style={{ ...updatePartialState() }}
                      />
                    ))
                : null}
            </svg>
          </Box>
          <Box
            onClick={fullBoxClick}
            overflow="hidden"
            position="relative"
            height="100%"
            sx={{
              // backgroundImage: `url(${image.uri})`,
              backgroundImage: isSpecificImage ? `url(${image.uri})` : `image-set(
                url("${image?.uri}/${image?.name}_${9600}x.avif") 2x type("image/avif"),
                url("${image?.uri}/${image?.name}_${9600}x.webp") 2x type("image/webp"),
                url("${image?.uri}/${image?.name}_${1200}x.avif") 1x type("image/avif"),
                url("${image?.uri}/${image?.name}_${1200}x.webp") 1x type("image/webp"),
                url("${image?.uri}/${image?.name}_${1200}x.jpg") 1x type("image/jpeg"),
                url("${image?.uri}/${image?.name}_${9600}x.jpg") 2x type("image/jpeg")
              )`,
              // backgroundSize: "100%",
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              cursor: "pointer",
              ":hover .MuiButtonBase-root": {
                opacity: "1",
              },
              "::before": {
                ...(showImage ? {} : hidingStyle),
                content: "''",
                position: "absolute",
                width: "100%",
                height: "100%",
                pointerEvents: "none",
              },
            }}
          >
            <picture
              style={{
                ...(!showImage ? hidingStyle : {}),
                // position: 'absolute',
                // top: "50%",
                // left: "50%",
                // transform: `translate(-50%, -50%)`,
                visibility: "hidden",
                opacity: 0,
              }}
            >
              <img
                src={isSpecificImage ? image.uri : `${image.uri}/${image.name}_${1200}x.webp`}
                alt={`${image.description ?? ""}`}
                width="100%"
              />
            </picture>
          </Box>
        </span>
      </Tooltip>
    </Box>
  );
};

function toggleShowImage(userShown: ObfuscationState, hasExclusions: boolean) {
  // Show the image if either the user has elected to show the image either completely or partially
  if (userShown === "on" || userShown === "partial") {
    return true;
  }

  // When the user has not elected to the show the image, ony the show images that have no exclusions
  return !hasExclusions;
}

// function containsExclusions(image?: TaggedImage | undefined, tagExclusions?: Array<Tag> | undefined): boolean {
//   if (!image) {
//     return false;
//   }

//   if (!tagExclusions?.length) {
//     return true;
//   }

//   return !image.tags.some((tag) =>
//     tagExclusions.find(
//       (exclusion) =>
//         exclusion.id === tag.id && tag.magnitude > exclusion.magnitude
//     )
//   );
// }

function containsExclusions(
  image?: TaggedImage | undefined,
  tagExclusions?: Array<Tag> | undefined
): boolean {
  // If there is no image, there can't be an exclusions since there is nothing to show the user
  if (!image) {
    return false;
  }

  // if there are not tagExlusions or the tagExclusions are empty, then no exclusions (obviously)
  if (!tagExclusions?.length) {
    return false;
  }

  // If there are not tags on the image, then there can be no exclusions because not comparison can take place
  if (!image.tags.length) {
    return false;
  }

  // There are tag exclusions when there is at least one:
  // 1. exclusions whose if matches a tag
  // 2. AND there is either
  //    a. no magnitude provided by the exclusion (meaning exclude everything)
  //    b. OR the magnitude of the tag is greater than the magnitude in the exclusion
  return image.tags.some((tag) =>
    tagExclusions.find(
      (exclusion) =>
        exclusion.id === tag.id &&
        (!exclusion.magnitude || tag.magnitude > exclusion.magnitude)
    )
  );
}

function listExclusions(
  image?: TaggedImage | undefined,
  tagExclusions?: Array<Tag> | undefined
): Array<Tag> {
  if (!image) {
    return [];
  }

  if (!tagExclusions?.length) {
    return [];
  }

  if (!image.tags.length) {
    return [];
  }

  return image.tags.filter(
    (tag) =>
      tagExclusions.findIndex(
        (exclusion) =>
          exclusion.id === tag.id &&
          (!exclusion.magnitude || tag.magnitude > exclusion.magnitude)
      ) !== -1
  );
}

function updateImageStyle(obfuscationMethod: ObfuscationStyle) {
  if (typeof obfuscationMethod === "number") {
    return { backdropFilter: `blur(${obfuscationMethod}px)` };
  }

  if (obfuscationMethod === "hide") {
    return { backdropFilter: "brightness(0)" };
  }

  if (typeof obfuscationMethod === "string" && obfuscationMethod !== "blur") {
    return {};
  }

  return { backdropFilter: "blur(5px)" };
}

export default PostImage;
